import { find, flatten, isEmpty, min, max, set, sortBy, cloneDeep, uniqBy } from 'lodash'
import moment from 'moment'

import { DEFAULT_PLANNING_DELAY, startDateFormatString, VISIT_STATUSES } from '../js/constants'
import '../types'
import {
  getAllQuayLoadstations,
  getLoadstationsBetweenBollards,
  getMatchingProducts,
  isEtaStillValid,
  roundToMinutes
} from './general'
import { createVisitOrderPairs, hasParallelOrders } from './terminalVisitUtils'
import { categorizeVisits } from './terminalPlannerUtils'

export const CONFLICT_CATEGORIES = {
  BOLLARD: 'Bollard conflict',
  BOARD_BOARD: 'Board-board conflict',
  CARGO: 'Cargo conflict',
  CARGO_ETA: 'Cargo ETA conflict',
  ATA_CARGO_END_TIME: 'ATA and cargo end time conflict',
  ATA_ATD_CARGO: 'ATA / ATD cargo times conflict',
  VISIT_ORDERS: 'Visit orders conflict'
}

/**
 * Returns all conflicts of all visits
 */
export function getConflicts(
  terminalVisits,
  quays,
  etaTooLateThreshold,
  planningDelayTolerance = DEFAULT_PLANNING_DELAY
) {
  const conflicts = {}
  const externalConflicts = {}

  const { mainQuayVisits, externalQuayVisits } = categorizeVisits(quays, terminalVisits)

  mainQuayVisits.forEach(currentVisit => {
    currentVisit.orders.forEach(currentOrder => {
      const newConflicts = getConflictsForOneTerminalVisit(
        mainQuayVisits,
        currentVisit,
        currentOrder,
        quays,
        etaTooLateThreshold,
        planningDelayTolerance
      )

      if (newConflicts) {
        set(conflicts, [currentVisit._id, currentOrder.uuid], newConflicts)
      }
    })
  })

  externalQuayVisits.forEach(currentVisit => {
    currentVisit.orders.forEach(currentOrder => {
      const newConflicts = getConflictsForOneExternalTerminalVisit(
        externalQuayVisits,
        currentVisit,
        currentOrder,
        quays,
        etaTooLateThreshold,
        planningDelayTolerance
      )

      if (newConflicts) {
        set(externalConflicts, [currentVisit._id, currentOrder.uuid], newConflicts)
      }
    })
  })

  return { conflicts, externalConflicts }
}

/**
 * Returns a map with the terminal visits which do have overlap with an other
 * visit.
 * @param {Array} terminalVisits  List with all terminal visits
 * @return {Object.<string, Object.<string, boolean>>}
 *            Object with the terminal visit id's that have
 *            overlap as first key, and the order uuid as second key
 *            with value true.
 */
export function getOverlappingTerminalVisits(terminalVisits, quays) {
  const { mainQuayVisits } = categorizeVisits(quays, terminalVisits)

  /** @type {Object.<string, Object.<string, boolean>>} */
  const overlappingVisits = {}

  // create pairs of each order+visit
  const pairs = createVisitOrderPairs(mainQuayVisits)

  pairs.forEach((pair1, index) => {
    const fromIndex = index + 1 // only search in the visits lower down  in the list
    const match = find(
      pairs,
      pair2 => {
        return (
          overlappingBollards(pair1.order, pair2.order) &&
          partlyOverlappingInTime(pair1.visit, pair1.order, pair2.visit, pair2.order) &&
          (pair1.visit._id !== pair2.visit._id || !hasParallelOrders(pair1.visit))
        )
      },
      fromIndex
    )

    if (match) {
      set(overlappingVisits, [pair1.visit._id, pair1.order.uuid], true)
      set(overlappingVisits, [match.visit._id, match.order.uuid], true)
    }
  })

  return overlappingVisits
}

/**
 * Returns a map with the terminal visits which do have overlap with an other
 * visit.
 * @param {Array} terminalVisits  List with all terminal visits
 * @return {Object.<string, Object.<string, boolean>>}
 *            Object with the terminal visit id's that have
 *            overlap as first key, and the order uuid as second key
 *            with value true.
 */
export function getOverlappingExternalTerminalVisits(terminalVisits, quays) {
  const { externalQuayVisits } = categorizeVisits(quays, terminalVisits)

  /** @type {Object.<string, Object.<string, boolean>>} */
  const overlappingExternalVisits = {}

  // create pairs of each order+visit
  const externalPairs = createVisitOrderPairs(externalQuayVisits)

  externalPairs.forEach((pair1, index) => {
    const fromIndex = index + 1 // only search in the visits lower down  in the list
    const match = find(
      externalPairs,
      pair2 => {
        return (
          overlappingBollards(pair1.order, pair2.order) &&
          partlyOverlappingInTime(pair1.visit, pair1.order, pair2.visit, pair2.order) &&
          (pair1.visit._id !== pair2.visit._id || !hasParallelOrders(pair1.visit))
        )
      },
      fromIndex
    )

    if (match) {
      set(overlappingExternalVisits, [pair1.visit._id, pair1.order.uuid], true)
      set(overlappingExternalVisits, [match.visit._id, match.order.uuid], true)
    }
  })

  return overlappingExternalVisits
}

/**
 * Returns conflicting visits for one terminalVisit
 * returns undefined if there are no conflicts
 */
export function getConflictsForOneTerminalVisit(
  terminalVisits,
  currentVisit,
  currentOrder,
  quays,
  etaTooLateThreshold,
  planningDelayTolerance = DEFAULT_PLANNING_DELAY
) {
  const conflicts = flatten([
    checkBollardConflicts(terminalVisits, currentVisit, currentOrder),
    checkBoardBoardConflict(terminalVisits, currentVisit, currentOrder),
    checkCargoConflict(currentVisit, currentOrder, quays),
    checkEtaCargoConflict(currentVisit, currentOrder, etaTooLateThreshold),
    checkAtaCargoEndTimeConflict(currentVisit, currentOrder, planningDelayTolerance)
  ])

  return !isEmpty(conflicts) ? conflicts : undefined
}

/**
 * Returns conflicting visits for one terminalVisit
 * returns undefined if there are no conflicts
 */
export function getConflictsForOneExternalTerminalVisit(
  terminalVisits,
  currentVisit,
  currentOrder,
  quays,
  etaTooLateThreshold,
  planningDelayTolerance = DEFAULT_PLANNING_DELAY
) {
  const conflicts = flatten([
    checkBoardBoardConflict(terminalVisits, currentVisit, currentOrder),
    checkEtaCargoConflict(currentVisit, currentOrder, etaTooLateThreshold),
    checkAtaCargoEndTimeConflict(currentVisit, currentOrder, planningDelayTolerance)
  ])

  return !isEmpty(conflicts) ? conflicts : undefined
}

/**
 * Check for bollard conflicts
 * I.E. two visits that are assigned overlapping bollard numbers within the same timeframe
 * When a terminal visit is marked as boardBoard operation, it is ignored.
 * @param {TerminalVisit[]} terminalVisits
 * @param {TerminalVisit} currentVisit
 * @param {Order} currentOrder
 */
export function checkBollardConflicts(terminalVisits, currentVisit, currentOrder) {
  // board-board is allowed to overlap with other visits
  if (currentVisit.boardBoard) {
    return []
  }

  return flatten(
    terminalVisits
      .filter(
        visit =>
          // don't compare board-board orders
          !visit.boardBoard
      )
      .map(visit => {
        // dont compare the same orders
        const ordersWithoutCurrentOrder = visit.orders.filter(order => order.uuid !== currentOrder.uuid)

        const visitConflicts = ordersWithoutCurrentOrder
          .filter(
            order =>
              overlappingBollards(order, currentOrder) &&
              partlyOverlappingInTime(visit, order, currentVisit, currentOrder)
          )
          .map(order => {
            const ship = visit.ship.name ? visit.ship.name : 'ship with length ' + visit.ship.length + ' m'

            // all items in orderList should have same start and end bollards (from earlier grouping)
            const conflictingStartBollard = Math.max(order.bollardStart, currentOrder.bollardStart)
            const conflictingEndBollard = Math.min(order.bollardEnd, currentOrder.bollardEnd)

            const conflictingTimeStart = Math.max(
              getAtaOrCargoStart(visit, order),
              getAtaOrCargoStart(currentVisit, currentOrder)
            )
            const conflictingTimeEnd = Math.min(
              getAtdOrCargoEnd(visit, order),
              getAtdOrCargoEnd(currentVisit, currentOrder)
            )

            const timeStart = moment(conflictingTimeStart).format(startDateFormatString)
            const timeEnd = moment(conflictingTimeEnd).format(startDateFormatString)

            const firstPartOfMessage =
              visit._id !== currentVisit._id
                ? `Overlapping visit from ${ship} on bollards `
                : `Overlapping ${order.product} order on bollards `

            return {
              category: CONFLICT_CATEGORIES.BOLLARD,
              description:
                firstPartOfMessage +
                `${conflictingStartBollard} - ${conflictingEndBollard} ` +
                `from ${timeStart} until ${timeEnd}.`,
              conflictingStartBollard,
              conflictingEndBollard,
              conflictingTimeStart,
              conflictingTimeEnd,
              conflictingTerminalVisit: visit,
              orderUuid: order.uuid
            }
          })
        return uniqBy(visitConflicts, 'description')
      })
  )
}

/**
 * Test whether any of the orders of two visits have overlapping bollards
 * @param {Order} order
 * @param {Object} orderToCheck
 * @returns {boolean}
 */
export function overlappingBollards(order, orderToCheck) {
  if (order.bollardStart < orderToCheck.bollardEnd && order.bollardEnd > orderToCheck.bollardStart) {
    return true
  }

  return false
}

/**
 * Checks if a specified order overlaps any orders in a list
 * @param {object} visit1 visit for orderList
 * @param {Object} order list of orders being checked against
 * @param {object} visit2 visit for order being checked
 * @param {object} orderToCheck order to check
 * @returns {boolean}
 */
export function partlyOverlappingInTime(visit1, order, visit2, orderToCheck) {
  // Orders in the same visit should only check the order start and end date in same parallelGroup.
  if (visit1._id === visit2._id) {
    if (
      order.parallelGroup === orderToCheck.parallelGroup &&
      order.bollardStart === orderToCheck.bollardStart &&
      order.bollardEnd === orderToCheck.bollardEnd
    ) {
      return partlyOverlappingOrdersInTime(order, orderToCheck)
    }
    if (order.bollardStart !== orderToCheck.bollardStart && order.bollardEnd !== orderToCheck.bollardEnd) {
      return partlyOverlappingOrdersInTime(order, orderToCheck)
    }
    return false
  }

  if (
    getAtaOrCargoStart(visit1, order) < getAtdOrCargoEnd(visit2, orderToCheck) &&
    getAtdOrCargoEnd(visit1, order) > getAtaOrCargoStart(visit2, orderToCheck)
  ) {
    return true
  }

  return false
}

/**
 * Checks if a specified order overlaps any orders in a list that is in the same parallelGroup
 * @param {Object} order list of orders being checked against
 * @param {object} orderToCheck order to check

 * @returns {boolean}
 */
export function partlyOverlappingOrdersInTime(order, orderToCheck) {
  if (order.cargoStart < orderToCheck.cargoEnd && order.cargoEnd > orderToCheck.cargoStart) {
    return true
  }

  return false
}

export function fullyOverlappingInTime(outerVisit, outerOrder, innerVisit, innerOrder) {
  return (
    getAtaOrCargoStart(innerVisit, innerOrder) >= getAtaOrCargoStart(outerVisit, outerOrder) &&
    getAtdOrCargoEnd(innerVisit, innerOrder) <= getAtdOrCargoEnd(outerVisit, outerOrder)
  )
}

/**
 * Format orders to newTimeBetweenPrevOrder, newDuration, newStart
 * @param {number} ata
 * @param {number} atd
 * @param {Order[]} orders
 * @returns {{newStart, newDuration, newTimeBetweenPrevOrder, newEnd, uuid}[]}
 */
export function formatOrdersWithNewAtaAndAtd(ata, atd, orders) {
  let prevOrderEnd

  // format orders to newTimeBetweenPrevOrder, newDuration, newStart
  const sortedOrders = sortBy(orders, 'cargoStart')
  return cloneDeep(sortedOrders).map((order, index) => {
    const orderDuration = order.cargoEnd - order.cargoStart
    const actualVisitDuration = atd - ata
    const estimatedVisitDuration = sortedOrders[sortedOrders.length - 1].cargoEnd - sortedOrders[0].cargoStart
    const newDuration = (actualVisitDuration / estimatedVisitDuration) * orderDuration
    const orderWaitingTimeBefore = order.cargoStart - (index === 0 ? 0 : sortedOrders[index - 1].cargoEnd)
    const newTimeBetweenPrevOrder = (actualVisitDuration / estimatedVisitDuration) * orderWaitingTimeBefore
    if (index === 0) {
      prevOrderEnd = ata + newDuration

      return {
        uuid: order.uuid,
        newStart: ata,
        newDuration,
        newTimeBetweenPrevOrder,
        newEnd: ata + newDuration
      }
    }
    const newStart = newTimeBetweenPrevOrder + prevOrderEnd

    prevOrderEnd = newDuration + newStart
    return {
      uuid: order.uuid,
      newStart,
      newDuration,
      newTimeBetweenPrevOrder,
      newEnd: newStart + newDuration
    }
  })
}

/**
 * Get the end time of the visit to be used for rendering the visit.
 * This can be atd, current time, or cargoEnd
 * @param {TerminalVisit} visit
 * @returns {number}
 */
export function getVisitStartTime(visit) {
  if (
    (visit.status === VISIT_STATUSES.moored || visit.status === VISIT_STATUSES.sailed) &&
    visit.ataTime !== undefined &&
    visit.ataTime !== null
  ) {
    return visit.ataTime
  }

  const minOrderStartTime = Math.min(...visit.orders.map(order => order.cargoStart))
  return minOrderStartTime
}

/**
 * Get the end time of the visit to be used for rendering the visit.
 * This can be atd, current time, or cargoEnd
 * @param {TerminalVisit} visit
 * @returns {number}
 */
export function getVisitEndTime(visit) {
  if (
    visit.status === VISIT_STATUSES.sailed &&
    visit.atdTime !== undefined &&
    visit.atdTime !== null &&
    visit.ataTime !== undefined &&
    visit.ataTime !== null
  ) {
    return visit.atdTime
  }
  const maxOrderEndTime = Math.max(...visit.orders.map(order => order.cargoEnd))
  if (
    visit.status === VISIT_STATUSES.moored &&
    visit.ataTime !== undefined &&
    visit.ataTime !== null &&
    maxOrderEndTime < moment().valueOf()
  ) {
    return moment().valueOf()
  }

  return maxOrderEndTime
}

/**
 * Get the end time of the visit to be used for rendering the visit.
 * This can be atd, current time, or cargoEnd
 * @param {TerminalVisit} visit
 * @returns {number}
 */
export function getAtaOrCargoStart(visit, order) {
  if (
    (visit.status === VISIT_STATUSES.moored || visit.status === VISIT_STATUSES.sailed) &&
    visit.ataTime !== undefined &&
    visit.ataTime !== null
  ) {
    const minCargoStart = min(visit.orders.map(ord => ord.cargoStart))

    if (minCargoStart === order.cargoStart) {
      return visit.ataTime < order.cargoStart ? visit.ataTime : order.cargoStart
    }
  }

  return order.cargoStart
}

/**
 * Get the end time of the visit to be used for rendering the visit.
 * This can be atd, current time, or cargoEnd
 * @param {TerminalVisit} visit
 * @returns {number}
 */
export function getAtdOrCargoEnd(visit, order) {
  const maxCargoEnd = max(visit.orders.map(ord => ord.cargoEnd))
  if (
    visit.status === VISIT_STATUSES.sailed &&
    visit.atdTime !== undefined &&
    visit.atdTime !== null &&
    visit.ataTime !== undefined &&
    visit.ataTime !== null
  ) {
    if (maxCargoEnd === order.cargoEnd) {
      return visit.atdTime > order.cargoEnd ? visit.atdTime : order.cargoEnd
    }
    return order.cargoEnd
  }
  const timeNow = roundToMinutes(moment().valueOf())

  if (visit.status === VISIT_STATUSES.moored && visit.ataTime !== undefined && visit.ataTime !== null) {
    if (maxCargoEnd === order.cargoEnd && maxCargoEnd < timeNow) {
      return timeNow
    }
  }

  return order.cargoEnd
}

/**
 * When `terminalVisit` is marked as board-board, it must overlap with
 * an other visit
 * @param {TerminalVisit[]} terminalVisits
 * @param {TerminalVisit} currentVisit
 * @param {Order} currentOrder
 */
export function checkBoardBoardConflict(terminalVisits, currentVisit, currentOrder) {
  if (!currentVisit.boardBoard) {
    return []
  }

  const hasOverlappingVisits = terminalVisits
    .filter(visit => visit._id !== currentVisit._id) // don't compare against itself
    .find(visit => {
      return visit.orders.find(
        order =>
          overlappingBollards(order, currentOrder) && fullyOverlappingInTime(visit, order, currentVisit, currentOrder)
      )
    })

  if (hasOverlappingVisits) {
    return []
  }

  return [
    {
      category: CONFLICT_CATEGORIES.BOARD_BOARD,
      description:
        'Visit is marked as a board-board visit. ' +
        'Therefore, there should be an other vessel being moored ' +
        'for the whole duration of the board-board visit.'
    }
  ]
}

/**
 * Check if there a conflict in the cargo of the visit:
 * visit is planned at bollards where product can not be loaded
 * @param {TerminalVisit} visit
 * @param {Order} order
 * @param {Array} quays
 */
export function checkCargoConflict(visit, order, quays) {
  if (!order.product || order.bollardStart == null || order.bollardEnd == null) {
    return []
  }

  const loadStations = quays.map(quay => getAllQuayLoadstations(quay)).reduce((acc, val) => acc.concat(val), [])

  // Find which loading stations the terminal visit is at
  const terminalVisitLoadStations = getLoadstationsBetweenBollards(loadStations, order.bollardStart, order.bollardEnd)

  const terminalVisitAtCargo = terminalVisitLoadStations.filter(
    loadStation =>
      loadStation.products && getMatchingProducts(order.product, order.operation, loadStation.products).length
  )

  if (terminalVisitAtCargo.length) {
    return []
  }

  return [
    {
      category: CONFLICT_CATEGORIES.CARGO,
      description: `Product of type "${order.product}" cannot be loaded at current visit bollards.`,
      conflictingTimeStart: order.cargoStart,
      conflictingTimeEnd: order.cargoEnd,
      conflictingVisitCargo: order // TODO: pass only relevant fields?
    }
  ]
}

/**
 * Check if there are any conflicting cargo and eta times
 * @param {TerminalVisit} visit
 * @param {Order} order
 * @param {number} etaTooLateThreshold
 */
export function checkEtaCargoConflict(visit, order, etaTooLateThreshold) {
  if (
    !visit.etaTime ||
    !isEtaStillValid(visit.etaTime, visit.etaTimeUpdatedAt) ||
    !order.cargoStart ||
    visit.ataTime ||
    visit.atdTime ||
    etaTooLateThreshold == null
  ) {
    return []
  }

  const timeDifferenceBetweenEtaAndCargoStart = visit.etaTime - order.cargoStart

  if (timeDifferenceBetweenEtaAndCargoStart <= etaTooLateThreshold) {
    return []
  }

  const timeEta = moment(visit.etaTime).format(startDateFormatString)
  const timeStart = moment(order.cargoStart).format(startDateFormatString)

  return [
    {
      category: CONFLICT_CATEGORIES.CARGO_ETA,
      description:
        `The ETA time of the vessel is ${timeEta} ` +
        `and the cargo starting time is ${timeStart}, ` +
        'this is too far apart from each other.',
      conflictingTimeStart: order.cargoStart,
      conflictingTimeEnd: order.cargoEnd,
      conflictingVisitCargo: order,
      conflictingTimeEta: visit.etaTime
    }
  ]
}

/**
 * Check if the cargo end time is before the ata time
 * @param {TerminalVisit} visit
 * @param {Order} order
 * @param {number} planningDelayTolerance
 */
export function checkAtaCargoEndTimeConflict(visit, order, planningDelayTolerance) {
  const ataInSec = roundToMinutes(visit.ataTime)
  const atdInSec = roundToMinutes(visit.atdTime)

  const orderStartInSec = roundToMinutes(order.cargoStart)
  const orderEndInSec = roundToMinutes(order.cargoEnd)

  if (visit.status === VISIT_STATUSES.moored && order.cargoEnd && visit.ataTime && orderEndInSec < ataInSec) {
    const timeEnd = moment(order.cargoEnd).format(startDateFormatString)
    const timeAta = moment(visit.ataTime).format(startDateFormatString)

    return [
      {
        category: CONFLICT_CATEGORIES.ATA_CARGO_END_TIME,
        description:
          `The cargo end time of the vessel is ${timeEnd}, ` +
          `which is before the ATA time ${timeAta}. ` +
          'We display the ATA time plus duration.',
        conflictingTimeEnd: order.cargoEnd,
        conflictingTimeAta: visit.ataTime
      }
    ]
  }

  const timeDifferenceBetweenAtaAndCargoStart = ataInSec - orderStartInSec
  const timeDifferenceBetweenAtdAndCargoEnd = orderEndInSec - atdInSec

  if (
    visit.status === VISIT_STATUSES.moored &&
    order.cargoEnd &&
    visit.ataTime &&
    timeDifferenceBetweenAtaAndCargoStart > planningDelayTolerance
  ) {
    // orderStartInSec < ataInSec
    const timeStart = moment(order.cargoStart).format(startDateFormatString)
    const timeAta = moment(visit.ataTime).format(startDateFormatString)

    return [
      {
        category: CONFLICT_CATEGORIES.ATA_ATD_CARGO,
        description:
          `The cargo start time of the vessel is ${timeStart}, which is before the ATA time ${timeAta}. ` +
          `You can right click on the order and choose "Shift start orders(s) to ATA" to shift the visit.`,
        conflictingTimeEnd: order.cargoEnd,
        conflictingTimeAta: visit.ataTime
      }
    ]
  }

  if (
    visit.status === VISIT_STATUSES.sailed &&
    order.cargoEnd &&
    visit.ataTime &&
    visit.atdTime &&
    (timeDifferenceBetweenAtaAndCargoStart > planningDelayTolerance ||
      timeDifferenceBetweenAtdAndCargoEnd > planningDelayTolerance)
  ) {
    // (orderStartInSec < ataInSec || orderEndInSec > atdInSec)
    const timeStart = moment(order.cargoStart).format(startDateFormatString)
    const timeAta = moment(visit.ataTime).format(startDateFormatString)
    const timeEnd = moment(order.cargoEnd).format(startDateFormatString)
    const timeAtd = moment(visit.atdTime).format(startDateFormatString)

    const cargoStartBeforeAta =
      timeDifferenceBetweenAtaAndCargoStart > planningDelayTolerance
        ? `The cargo start time of the vessel is ${timeStart}, which is before the ATA time ${timeAta}. ` +
          `You can right click on the order and choose "Match order times to ship times" to automaticly fit the orders in the visit ship ATA and ATD`
        : ''

    const cargoEndAfterAtd =
      timeDifferenceBetweenAtdAndCargoEnd > planningDelayTolerance
        ? `The cargo end time of the vessel is ${timeEnd}, which is after the ATD time ${timeAtd}`
        : ''

    return [
      {
        category: CONFLICT_CATEGORIES.ATA_ATD_CARGO,
        description: `${cargoEndAfterAtd}${cargoStartBeforeAta}`,

        conflictingTimeEnd: order.cargoEnd,
        conflictingTimeAta: visit.ataTime
      }
    ]
  }

  return []
}
